<template>
  <div class="card">
    <div class="card-header">
      <h1 class="card-title">
        Party Configuration
      </h1>
    </div>
    <div class="card-body">
      <div class="mb-3">
        <textarea
          id="config-textarea"
          v-model="configText"
          class="form-control font-monospace"
          rows="15"
        ></textarea>
      </div>

      <div v-if="validationError" class="alert alert-danger">
        <h6><i class="bi bi-exclamation-triangle-fill me-2"></i>Configuration Error</h6>
        <p class="mb-0">{{ validationError }}</p>
      </div>

      <div v-if="validationSuccess" class="alert alert-success">
        <h6><i class="bi bi-check-circle-fill me-2"></i>Party Created Successfully</h6>
        <p class="mb-0">Configuration validated and party is now active. Players can now join!</p>
      </div>

      <button
        @click="validate"
        :disabled="isValidating"
        class="btn btn-primary btn-lg w-100 mt-3"
      >
        {{ isValidating ? 'Creating Party...' : 'Create Party' }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'

interface Props {
  sampleConfig: any
}

interface Emits {
  (e: 'validate', config: string): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

// Component state
const configText = ref('')
const isValidating = ref(false)
const validationError = ref<string | null>(null)
const validationSuccess = ref(false)

// Initialize with sample config
onMounted(() => {
  configText.value = JSON.stringify(props.sampleConfig, null, 2)
})

// Validate configuration
function validate() {
  isValidating.value = true
  validationError.value = null
  validationSuccess.value = false

  try {
    // Emit validation request to parent
    emit('validate', configText.value)
    // Don't set success here - wait for parent response

  } catch (error) {
    validationError.value = error instanceof Error ? error.message : 'Validation failed'
    isValidating.value = false
  }
}

// Handle validation result from parent
function handleValidationResult(success: boolean, error?: string) {
  isValidating.value = false

  if (success) {
    validationSuccess.value = true
  } else {
    validationError.value = error || 'Validation failed'
  }
}

// Expose method for parent to call
defineExpose({
  handleValidationResult
})
</script>

<style scoped>
.font-monospace {
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
}
</style>
